import {
  getTablePermissions,
  getIgnoredColumnsByPermission,
  filterColumnsByPermission,
  hasPermission,
} from "@/lib/permissions";
import type { UserRoleType } from "@/types/auth";
import { router } from "expo-router";

export type MarketOption = { value: string; label: string };

const STATUS_OPTIONS = [
  { value: "POTENTIAL", color: "todo" },
  { value: "PENDING APPROVAL", color: "pending" },
  { value: "IN PROGRESS", color: "pending" },
  { value: "ON HOLD", color: "attention" },
  { value: "COMPLETED", color: "complete" },
  { value: "CONFLICTING", color: "attention" },
  { value: "LOST", color: "neutral" },
  { value: "CANCELED", color: "attention" },
] as const;

const BILLING_STATUS_OPTIONS = [
  { value: "PENDING", color: "neutral" },
  { value: "READY FOR BILLING", color: "todo" },
  { value: "BILLING IN PROGRESS", color: "pending" },
  { value: "PNL PENDING", color: "complete" },
] as const;

// Columns that require read permission on related entities
const RELATIONSHIP_COLUMNS = {
  client: "clients",
  location: "locations",
  requested_by_contact: "contacts",
  onsite_contact: "contacts",
  primary_contact: "contacts",
  markets: "markets",
} as const;

export const getProjectTableConfig = (
  userRoles: UserRoleType[] | null,
  marketOptions: MarketOption[] = [],
  displayNames?,
) => {
  const permissions = getTablePermissions(userRoles, "projects");

  // Get columns to hide based on relationship permissions
  const permissionIgnored = getIgnoredColumnsByPermission(userRoles, RELATIONSHIP_COLUMNS);

  // Build readonly array conditionally based on permissions
  const readonlyColumns: (string | [string, (key: string, value: any, row: any) => void])[] = [
    "id",
    "start_time",
    "end_time",
    "created_at",
  ];

  // Only add clickable client column if user has clients.read
  if (hasPermission(userRoles, "clients", "read")) {
    readonlyColumns.push([
      "client",
      (_key, _value, row) => row.client && router.setParams({ panel: "client", id: row.client.id }),
    ]);
  }

  // Only add clickable location column if user has locations.read
  if (hasPermission(userRoles, "locations", "read")) {
    readonlyColumns.push("location");
  }

  // Only add clickable contact columns if user has contacts.read
  if (hasPermission(userRoles, "contacts", "read")) {
    readonlyColumns.push(
      [
        "requested_by_contact",
        (_key, _value, row) =>
          row.requested_by_contact && router.setParams({ panel: "contact", id: row.requested_by_contact.id }),
      ],
      [
        "onsite_contact",
        (_key, _value, row) => row.onsite_contact && router.setParams({ panel: "contact", id: row.onsite_contact.id }),
      ],
      [
        "primary_contact",
        (_key, _value, row) =>
          row.primary_contact && router.setParams({ panel: "contact", id: row.primary_contact.id }),
      ],
    );
  }

  const allFilterChoices = [
    {
      name: "id",
      kind: "text" as const,
      displayName: `${displayNames?.projects?.name ?? "Project"} Id`,
      menus: ["filter", "visible"] as const,
    },
    { name: "name", kind: "text" as const, menus: ["filter", "visible"] as const },
    {
      name: "status",
      kind: "enum" as const,
      options: [...STATUS_OPTIONS],
      menus: ["filter", "visible", "groupby", "orderby"] as const,
    },
    {
      name: "billing_status",
      kind: "enum" as const,
      options: [...BILLING_STATUS_OPTIONS],
      menus: ["filter", "visible", "orderby", "groupby"] as const,
    },
    {
      name: "deposit_amount",
      kind: "number" as const,
      displayName: "Deposit Amount",
      menus: ["filter", "visible", "orderby"] as const,
    },
    {
      name: "client",
      displayName: `${displayNames?.clients?.name ?? "Client"}`,
      kind: "linked_record" as const,
      menus: ["filter", "visible", "groupby", "orderby"] as const,
      linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
    },
    {
      name: "requested_by_contact",
      displayName: `Requested By ${displayNames?.contacts?.name ?? "Contact"}`,
      kind: "linked_record" as const,
      menus: ["filter", "visible", "groupby", "orderby"] as const,
      linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
    },
    {
      name: "onsite_contact",
      displayName: `Onsite ${displayNames?.contacts?.name ?? "Contact"}`,
      kind: "linked_record" as const,
      menus: ["filter", "visible", "groupby", "orderby"] as const,
      linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
    },
    {
      name: "primary_contact",
      displayName: `Primary ${displayNames?.contacts?.name ?? "Contact"}`,
      kind: "linked_record" as const,
      menus: ["filter", "visible", "groupby", "orderby"] as const,
      linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
    },
    {
      name: "location",
      displayName: displayNames?.locations?.name ?? "Location",
      kind: "linked_record" as const,
      menus: ["filter", "visible", "groupby", "orderby"] as const,
      linkedRecord: { labelKey: "name", valueKey: "id", truncate: 6 },
    },
    {
      name: "markets",
      kind: "array" as const,
      displayName: "Markets",
      options: marketOptions,
      menus: ["filter", "visible", "groupby"] as const,
    },
    { name: "start_time", kind: "date" as const, menus: ["filter", "visible", "orderby"] as const },
    { name: "end_time", kind: "date" as const, menus: ["filter", "visible", "orderby"] as const },
    { name: "created_at", kind: "date" as const, menus: ["filter", "visible", "orderby"] as const },
  ];

  return {
    entityName: "Project",
    entityNamePlural: "Projects",

    tableConfig: {
      headerFunc: (name: string) => {
        switch (name) {
          case "id":
            return `${displayNames?.projects?.name ?? "Project"} Id`;
          case "client":
            return displayNames?.clients?.name ?? "Client";
          case "requested_by_contact":
            return `Requested By ${displayNames?.contacts?.name ?? "Contact"}`;
          case "onsite_contact":
            return `Onsite ${displayNames?.contacts?.name ?? "Contact"}`;
          case "primary_contact":
            return `Primary ${displayNames?.contacts?.name ?? "Contact"}`;
          case "location":
            return displayNames?.locations?.name ?? "Location";
          case "deposit_amount":
            return "Deposit Amount";
          default:
            return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
        }
      },
      ignore: [
        "location_id",
        "tenant_id",
        "comments",
        "shifts",
        "requested_by",
        "meta_data",
        "locationMarket",
        ...permissionIgnored,
      ],
      readonly: readonlyColumns,
      validators: {},
      cellTextCallback: (value: any, key: string) => {
        if (value == null) return "";
        if (key === "id") return value.toString().slice(0, 6).toUpperCase();
        if (key === "start_time" || key === "end_time") return value; // let datetimeprovider handle formatting
        return typeof value === "string" ? value : JSON.stringify(value);
      },
      dataTypes: {
        created_at: "date",
        start_time: "date-time",
        end_time: "date-time",
        status: {
          type: "enum",
          options: STATUS_OPTIONS,
        },
        billing_status: {
          type: "enum",
          options: BILLING_STATUS_OPTIONS,
        },
        deposit_amount: "currency" as const,
        client: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        requested_by_contact: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        onsite_contact: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        primary_contact: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        location: {
          type: "linked_record" as const,
          labelKey: "name",
          valueKey: "id",
          truncate: 6,
        },
        // Only include markets config if user has markets.read permission
        ...(hasPermission(userRoles, "markets", "read") && {
          markets: {
            type: "array" as const,
            picker: "market" as const,
            chipColor: "#4169E1",
            options: marketOptions,
            readonlyFromField: "locationMarket",
          },
        }),
      },
      writeKeys: {
        billing_status: "meta_data.billing_status",
        deposit_amount: "meta_data.deposit_amount",
        // Only include markets writeKey if user has markets.read permission
        ...(hasPermission(userRoles, "markets", "read") && {
          markets: "meta_data.markets",
        }),
      },
    },

    filterChoices: filterColumnsByPermission(userRoles, allFilterChoices, RELATIONSHIP_COLUMNS),
    ...permissions,
    storageKey: "project_flyout",
  };
};
